import _ from 'underscore';

import { FeatureFlags } from '~src/ts/FeatureFlags';

import { CatalogContentType, CatalogContentTypes } from '../../catalog/CatalogContentType';
import { ExplorableItemType, SpaceInfoItemType } from '../../ts/models/ContentType';
import { ISpaceTypeInfo } from '../../ts/models/resourceInfoParts';
import { NavLocation } from '../layout/NavLocation';
import { SiteSpaceContentType as SiteSubspaceName } from './SiteSpaceContentTypeState';


// DON'T ADD IMPORTS FROM CONTENTDEFINITION HERE. IT CAUSES BUILD ERRORS FOR SOME REASON.
// This is probably because these interfaces are being depended upon from the integration tests,
// which use a stricter tsconfig than the main source code does.
export type AllServerUsersSubspaceName = 'list' | 'identities';
export type AllSiteUsersSubspaceName = 'list' | 'identities';
export type DatasourceSubspaceName = 'nlp' | 'connections' | 'extractRefreshes' | 'remoteRefreshSchedules' | 'connectedWorkbooks' | 'lineage' | 'askDataUsageAnalytics';
export type FlowSubspaceName = 'overview' | 'connections' | 'scheduledTasks' | 'runHistory' | 'subscriptions' | 'lineage';
export type PublishedConnectionSubspaceName = 'tables' | 'scheduledTasks' | 'lineage';
export type PublishedConnectionTableSubspaceName = 'overview' | 'scheduledTasks' | 'lineage';
export type WorkbookSubspaceName = 'connectedMetrics' | 'customViews' | 'dataconnections' | 'datasources' | 'extractTasks' | 'lineage' | 'subscriptions' | 'views' | 'workbookAccelerations';
export type SearchSubspaceName = 'views' | 'metrics' | 'workbooks' | 'datasources' | 'projects' | 'flows' | 'tablesWithCrass' | 'databasesWithCrass' | 'tables' | 'databases' | 'dataRoles' | 'lenses' | 'users' | 'publishedConnections';
export type DataRoleSubspaceName = 'definition' | 'connectedFlows' | 'connectedDatasources';
export type LensSubspaceName = 'askData' | 'usageAnalytics';
export type MetricsSubspaceName = 'favorites' | 'all' | 'owned';
export type GroupSubspaceName = 'users' | 'details';
export type SiteUserSubspaceName = 'content' | 'alerts' | 'subscriptions' | 'collections' | 'settings';
// legacy subspace definitions only exist for previous bookmarked spaces. no new values should be added
export type LegacySiteUserSubspaceName = 'projects' | 'workbooks' | 'views' | 'metrics' | 'datasources' | 'dataconnections' | 'flows';
export type SiteTasksSubspaceName = 'extractRefreshes' | 'runFlowTasks' | 'subscriptions' | 'alerts' | 'dataAcceleration' | 'linkedTasks';
export type ServerTasksSubspaceName = 'extractRefreshes' | 'runFlowTasks' | 'subscriptions' | 'dataAcceleration' | 'linkedTasks';
export type ScheduleSubspaceName = 'extractRefreshes' | 'subscriptions' | 'flows' | 'details';
export type SiteSettingsSubspaceName = 'settings' | 'extensions' | 'bridge' | 'authentication' | 'integrations' | 'connectedApplications';
export type ServerSettingsSubspaceName = 'settings' | 'extensions' | 'addsite' | 'licenses' | 'customization' | 'integrations' | 'resourcesgovernance'| 'connectedApplications';
export type CollectionsSubspaceName = 'all' | 'my';

export type SubspaceName =
  WorkbookSubspaceName |
  DatasourceSubspaceName |
  FlowSubspaceName |
  SiteSubspaceName |
  DataRoleSubspaceName |
  LensSubspaceName |
  SearchSubspaceName |
  MetricsSubspaceName |
  PublishedConnectionSubspaceName |
  PublishedConnectionTableSubspaceName |
  GroupSubspaceName |
  SiteUserSubspaceName |
  SiteTasksSubspaceName |
  ScheduleSubspaceName |
  SiteSettingsSubspaceName |
  ServerSettingsSubspaceName |
  ServerTasksSubspaceName |
  AllServerUsersSubspaceName |
  AllSiteUsersSubspaceName |
  CollectionsSubspaceName;
export const NullProjectIdForNonProjects: string = null;

// all content types need to have a space
export type SpaceType = SpaceInfoItemType | 'site' | 'sites' | 'sitesettings' | 'serversettings' | 'schedules' | 'favorites' | 'recents' |
  'home' | 'search' | 'unifiedSearch' | 'backgroundJobs' | 'siteuser' | 'siteusers' | 'serveruser' | 'serverusers' | 'recommendations' |
  'metric' | 'sitestatus' | 'sitetasks' | 'siteanalysis' | 'serveranalysis' | 'serverstatus' | 'servertasks' | 'catalog' | 'groups' |
  'revisionhistory' | 'servertasks' | 'collections' | 'starters' | 'sharedWithMe' | 'personal' | 'externalAssets';
export interface SpaceInfo {
  item: ISpaceTypeInfo;
  spaceType: SpaceType;
  subspaceName: SubspaceName;
  navLocationOverride?: NavLocation;
}

export const subspaceNameToCatalogContentType = (subspaceName: SubspaceName): CatalogContentType => {
  if (CatalogContentTypes.hasOwnProperty(subspaceName)) {
    const matchingTypes = Object.entries(CatalogContentTypes)
      .filter(([key, value]) => key === subspaceName)
      .map(([key, value]) => value);

    if (matchingTypes.length > 0) {
      return matchingTypes[0];
    }
  }

  switch (subspaceName) {
    case 'databases':
      return 'database';
    case 'tables':
      return 'table';
    case 'databasesWithCrass':
      return 'databaseWithCrass';
    case 'tablesWithCrass':
      return 'tableWithCrass';
  }

  throw new Error('No catalog content type mapping for provided subspace name');
};

// TODO: Add subspace configuration object (TFS: 827255)
// Maps a Workbook sub-space to a ExplorableItemType.
export const workbookSubspaceNameToExplorableItemType = (workbookSubSpaceName: WorkbookSubspaceName): ExplorableItemType | '' => {
  if (workbookSubSpaceName === 'customViews') {
    return 'customView';
  } else if (workbookSubSpaceName === 'dataconnections') {
    return 'dataconnection';
  } else if (workbookSubSpaceName === 'datasources') {
    return 'datasource';
  } else if (workbookSubSpaceName === 'extractTasks') {
    return 'extractTask';
  } else if (workbookSubSpaceName === 'connectedMetrics') {
    return 'metric';
  } else if (workbookSubSpaceName === 'subscriptions') {
    return 'subscription';
  } else if (workbookSubSpaceName === 'views') {
    return 'view';
  } else if (workbookSubSpaceName === 'workbookAccelerations') {
    return 'workbookAcceleration';
  }

  return '';
};

export const serverSettingsSubspaceNameToExplorableItemType = (subSpaceName: ServerSettingsSubspaceName): ExplorableItemType | '' => {
  if (subSpaceName === 'connectedApplications') {
    return 'connectedApplication';
  } else if (subSpaceName === 'resourcesgovernance') {
     return 'resourceGovernanceSetting';
  }

  return '';
};

export const siteSettingsSubspaceNameToExplorableItemType = (subSpaceName: SiteSettingsSubspaceName): ExplorableItemType | '' => {
  if (subSpaceName === 'connectedApplications') {
    return 'connectedApplication';
  }

  return '';
};

// TODO: Add subspace configuration object (TFS: 827255)
// Maps a Datasource sub-space to a ExplorableItemType.
export const datasourceSubspaceNameToExplorableItemType = (datasourceSubSpaceName: DatasourceSubspaceName): ExplorableItemType | '' => {
  if (datasourceSubSpaceName === 'connections') {
    return 'connection';
  } else if (datasourceSubSpaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (datasourceSubSpaceName === 'remoteRefreshSchedules') {
    return 'remoteRefreshSchedule';
  } else if (datasourceSubSpaceName === 'connectedWorkbooks') {
    return 'connectedWorkbook';
  }

  return '';
};

export const dataRoleSubspaceNameToExplorableItemType = (dataRoleSubSpaceName: DataRoleSubspaceName): ExplorableItemType | '' => {
  if (dataRoleSubSpaceName === 'connectedFlows') {
    return 'flow';
  }
  if (dataRoleSubSpaceName === 'connectedDatasources') {
    return 'datasource';
  }

  return '';
};

export const contentTypeToWorkbookSubspaceName = (contentType: ExplorableItemType): WorkbookSubspaceName | '' => {
  if (contentType === 'view') {
    return 'views';
  } else if (contentType === 'datasource') {
    return 'datasources';
  } else if (contentType === 'dataconnection') {
    return 'dataconnections';
  }

  return '';
};

export const searchSubspaceNameToExplorableItemType = (searchSubspaceName: SearchSubspaceName): ExplorableItemType | '' => {
  if (searchSubspaceName === 'views') {
    return 'view';
  } else if (searchSubspaceName === 'metrics') {
    return 'metric';
  } else if (searchSubspaceName === 'workbooks') {
    return 'workbook';
  } else if (searchSubspaceName === 'datasources') {
    return 'datasource';
  } else if (searchSubspaceName === 'flows') {
    return 'flow';
  } else if (searchSubspaceName === 'projects') {
    return 'project';
  } else if (searchSubspaceName === 'tables') {
    return 'table';
  } else if (searchSubspaceName === 'databases') {
    return 'database';
  } else if (searchSubspaceName === 'tablesWithCrass') {
    return 'tableWithCrass';
  } else if (searchSubspaceName === 'databasesWithCrass') {
    return 'databaseWithCrass';
  } else if (searchSubspaceName === 'dataRoles') {
    return 'dataRole';
  } else if (searchSubspaceName === 'lenses') {
    return 'lens';
  } else if (searchSubspaceName === 'users') {
    return 'searchuser';
  } else if (searchSubspaceName === 'publishedConnections') {
    return 'publishedConnection';
  }
  return '';
};

export const scheduleSubspaceNameToExplorableItemType = (scheduleSubspaceName: ScheduleSubspaceName): ExplorableItemType | '' => {
  if (scheduleSubspaceName === 'subscriptions') {
    return 'subscription';
  } else if (scheduleSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (scheduleSubspaceName === 'flows') {
    return 'runFlowTask';
  }
  return '';
};

export const groupSubspaceNameToExplorableItemType = (groupSubspaceName: GroupSubspaceName): ExplorableItemType | '' => {
  if (groupSubspaceName === 'users') {
    return 'siteuser';
  }
  return '';
};

export const siteTasksSubspaceNameToExplorableItemType = (tasksSubspaceName: SiteTasksSubspaceName): ExplorableItemType | '' => {
  if (tasksSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (tasksSubspaceName === 'runFlowTasks') {
    return 'runFlowTask';
  } else if (tasksSubspaceName === 'subscriptions') {
    return 'subscription';
  } else if (tasksSubspaceName === 'alerts') {
    return 'dataalert';
  } else if (tasksSubspaceName === 'dataAcceleration') {
    return 'accelerationTask';
  } else if (tasksSubspaceName === 'linkedTasks') {
    return 'runFlowTask';
  }

  return '';
};

export const serverTasksSubspaceNameToExplorableItemType = (tasksSubspaceName: ServerTasksSubspaceName): ExplorableItemType | '' => {
  if (tasksSubspaceName === 'extractRefreshes') {
    return 'extractTask';
  } else if (tasksSubspaceName === 'runFlowTasks') {
    return 'runFlowTask';
  } else if (tasksSubspaceName === 'subscriptions') {
    return 'subscription';
  } else if (tasksSubspaceName === 'dataAcceleration') {
    return 'accelerationTask';
  } else if (tasksSubspaceName === 'linkedTasks') {
    return 'runFlowTask';
  }

  return '';
};

const siteUserSubspaceNameToExplorableItemTypeMap: Record<SiteUserSubspaceName, ExplorableItemType | ''> = {
  content: '',
  alerts: 'dataalert',
  subscriptions: 'subscription',
  collections: 'collection',
  settings: '',
};

export const siteUserSubspaceNameToExplorableItemType = (siteUserSubspaceName: SiteUserSubspaceName): ExplorableItemType | '' => {
  if (_.has(siteUserSubspaceNameToExplorableItemTypeMap, siteUserSubspaceName)) {
    return siteUserSubspaceNameToExplorableItemTypeMap[siteUserSubspaceName];
  }
  return '';
};

export const legacySiteUserSubspaceNameToExplorableItemType = (siteUserSubspaceName: LegacySiteUserSubspaceName): ExplorableItemType | '' => {
  if (siteUserSubspaceName === 'views') {
    return 'view';
  } else if (siteUserSubspaceName === 'metrics') {
    return 'metric';
  } else if (siteUserSubspaceName === 'workbooks') {
    return 'workbook';
  } else if (siteUserSubspaceName === 'datasources') {
    return 'datasource';
  } else if (siteUserSubspaceName === 'flows') {
    return 'flow';
  } else if (siteUserSubspaceName === 'projects') {
    return 'project';
  }
  return '';
};

export const publishedConnectionSubspaceNameToExplorableItemType = (publishedConnectionSubspaceName: PublishedConnectionSubspaceName): ExplorableItemType | '' => {
  if (publishedConnectionSubspaceName === 'tables') {
    return 'publishedConnectionTable';
  } else if (publishedConnectionSubspaceName === 'scheduledTasks') {
    return 'extractTask';
  }
  return '';
};

export const isSpace = (spaceInfo: SpaceInfo, spaceType: SpaceType): boolean => {
  return spaceInfo && spaceInfo.spaceType === spaceType;
};

export const IsWorkbookDatasourceSubspaceTab = (spaceInfo: SpaceInfo): boolean => {
  return spaceInfo && isWorkbookSpace(spaceInfo) && (spaceInfo.subspaceName === 'datasources' || spaceInfo.subspaceName === 'dataconnections');
};

export const IsWorkbookSubscriptionsSubspaceTab = (spaceInfo: SpaceInfo): boolean => {
  return spaceInfo && isWorkbookSpace(spaceInfo) && spaceInfo.subspaceName === 'subscriptions';
};

export const IsWorkbookDataAccelerationSubspaceTab = (spaceInfo: SpaceInfo): boolean => {
  return spaceInfo && isWorkbookSpace(spaceInfo) && spaceInfo.subspaceName === 'workbookAccelerations';
};

export function isProjectSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'project';
}

export function isMixedContentSpace(spaceInfo: SpaceInfo) {
  return isProjectSpace(spaceInfo) ||
    isFavoritesSpace(spaceInfo) ||
    isSiteUserContentSubspace(spaceInfo) ||
    isSharedWithMeSpace(spaceInfo) ||
    isSingleCollectionSpace(spaceInfo) ||
    isPersonalSpace(spaceInfo);
}

export function isDatasourceSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource';
}

export function isDatasourceConnectionSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource' && spaceInfo.subspaceName === 'connections';
}

export function isUsageAnalyticsSubspace(spaceInfo: SpaceInfo) {
  return (isSpace(spaceInfo, 'datasource') && spaceInfo.subspaceName === 'askDataUsageAnalytics') ||
    (isSpace(spaceInfo, 'lens') && spaceInfo.subspaceName === 'usageAnalytics');
}

export function isDatasourceConnectedWorkbookSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'datasource' && spaceInfo.subspaceName === 'connectedWorkbooks';
}

export function isFlowSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'flow';
}

export function isPublishedConnectionSpace(spaceInfo: SpaceInfo): boolean {
  return spaceInfo.spaceType === 'publishedConnection';
}

export function isPublishedConnectionTableSpace(spaceInfo: SpaceInfo): boolean {
  return spaceInfo.spaceType === 'publishedConnectionTable';
}

export function isDataRoleSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'dataRole';
}

export function isWorkbookSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'workbook';
}

export function isSiteSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'site';
}

export function isSiteDatasourceSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && isSiteSpace(spaceInfo) && (spaceInfo.subspaceName === 'datasource' || spaceInfo.subspaceName === 'dataconnection');
}

export function isSiteDataRoleSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && isSiteSpace(spaceInfo) && (spaceInfo.subspaceName === 'dataRole');
}

export function isFavoritesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'favorites';
}

export function isRecentsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'recents';
}

export function isRecommendationsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'recommendations';
}

export function isHomeSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'home';
}

export function isSomeKindaSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && (spaceInfo.spaceType === 'search' || spaceInfo.spaceType === 'unifiedSearch');
}

export function isSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'search';
}

export function isUnifiedSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'unifiedSearch';
}

export function isMetricSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'metric';
}

export function isBackgroundJobsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'backgroundJobs';
}

export function isServerUserSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serveruser';
}

export function isAllServerUsersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serverusers' && !isAllUserIdentitiesSubspace(spaceInfo);
}

export function isAllUserIdentitiesSubspace(spaceInfo: SpaceInfo) {
  return FeatureFlags.identityMigrationEnabled && spaceInfo && spaceInfo.subspaceName === 'identities';
}

export function isGroupUsersSubspace(spaceInfo: SpaceInfo) {
  return isGroupSpace(spaceInfo) && spaceInfo.subspaceName === 'users';
}

export function isSiteUsersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteusers' && !isAllUserIdentitiesSubspace(spaceInfo);
}

export function isSiteUserSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser';
}

export function isSiteUserContentSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser' && spaceInfo.subspaceName === 'content';
}

export function isSiteUserDataAlertsSubSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'siteuser' && spaceInfo.subspaceName === 'alerts';
}

export function isSiteUserCollectionsSubspace(spaceInfo: SpaceInfo) {
  return isSiteUserSpace(spaceInfo) && spaceInfo.subspaceName === 'collections';
}

export function isSiteAccelerationCollectionsSubspace(spaceInfo: SpaceInfo) {
  return isSiteTasksSpace(spaceInfo) && spaceInfo.subspaceName === 'dataAcceleration';
}

export function isSiteWorkbooksSubspace(spaceInfo: SpaceInfo) {
  return isSiteSpace(spaceInfo) && spaceInfo.subspaceName === 'workbook';
}

export function isSiteSettingsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sitesettings';
}

export function isServerSettingsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serversettings';
}
export function isGroupSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'group';
}

export function isAllSitesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sites';
}

export function isSiteTasksSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sitetasks';
}

export function isServerTasksSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'servertasks';
}

export function isAllSchedulesSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'schedules';
}

export function isAllGroupsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'groups';
}

export function isCollectionsSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'collections';
}

export function isMyCollectionsSubspace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'collections' && spaceInfo.subspaceName === 'my';
}

export function isSingleCollectionSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'collection';
}

export function isScheduleSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'schedule';
}

export function isVizSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'view');
}

export function isMetricDetailSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'metric') && spaceInfo.subspaceName === undefined;
}

export function isAdminVizSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'siteanalysis') || isSpace(spaceInfo, 'serveranalysis');
}

export function isDatabasesSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'search' && spaceInfo.subspaceName === 'databases';
}

export function isDatabasesWithCrassSearchSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'search') && spaceInfo.subspaceName === 'databasesWithCrass';
}

export function isTablesWithSearchSpace(spaceInfo: SpaceInfo) {
  return spaceInfo.spaceType === 'search' && spaceInfo.subspaceName === 'tables';
}

export function isTablesWithCrassSearchSpace(spaceInfo: SpaceInfo) {
  return isSpace(spaceInfo, 'search') && spaceInfo.subspaceName === 'tablesWithCrass';
}

export function isWorkbookSpaceWithLuid(spaceInfo: SpaceInfo) {
  return isWorkbookSpace(spaceInfo) && spaceInfo.item.luid;
}

export function isCatalogSearchSpace(spaceInfo: SpaceInfo): boolean {
  try {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return spaceInfo.spaceType === 'search' && catalogContentType != null;
  } catch (e) {
    return false;
  }
}

export function isCatalogSpace(spaceInfo: SpaceInfo): boolean {
  try {
    const catalogContentType = subspaceNameToCatalogContentType(spaceInfo.subspaceName);
    return spaceInfo.spaceType === 'catalog' && catalogContentType != null;
  } catch (e) {
    return false;
  }
}

export function isCatalogDatabaseWithCrassSpace(spaceInfo: SpaceInfo): boolean {
  if (FeatureFlags.externalAssetsWithCRASSEnabled) {
    return isCatalogSpace(spaceInfo) && spaceInfo.subspaceName === 'databaseWithCrass';
  }
  return false;
}

export function isCatalogTableWithCrassSpace(spaceInfo: SpaceInfo): boolean {
  if (FeatureFlags.externalAssetsWithCRASSEnabled) {
    return isCatalogSpace(spaceInfo) && spaceInfo.subspaceName === 'tableWithCrass';
  }
  return false;
}

export function isAllStartersSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'starters';
}

export function isSharedWithMeSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'sharedWithMe';
}

export function isPersonalSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'personal';
}

export function isResourceGovernanceSpace(spaceInfo: SpaceInfo) {
  return spaceInfo && spaceInfo.spaceType === 'serversettings' && spaceInfo.subspaceName === 'resourcesgovernance';
}

export function isConnectedApplicationSpace(spaceInfo: SpaceInfo) {
  return spaceInfo?.spaceType === 'sitesettings' && spaceInfo.subspaceName === 'connectedApplications';
}

export function isSpaceWithDrafts(spaceInfo: SpaceInfo) {
  return isRecentsSpace(spaceInfo) ||
    isFavoritesSpace(spaceInfo) ||
    isSingleCollectionSpace(spaceInfo) ||
    isSiteWorkbooksSubspace(spaceInfo) ||
    isSharedWithMeSpace(spaceInfo) ||
    isProjectSpace(spaceInfo) ||
    isSiteUserContentSubspace(spaceInfo) ||
    isDatasourceConnectedWorkbookSubSpace(spaceInfo);
}

// Used for spaces that are not on a specific item:
// eg: site/favorites/recents/home/catalog/search/jobs/sitestatus/sitetasks
export const SpaceItemForNonEntitySpace: ISpaceTypeInfo = {
  id: NullProjectIdForNonProjects,
  contentType: undefined  // this is not a content type!
};
